import React from "react";
import { motion } from "framer-motion";
import {
  Compass,
  ClipboardList,
  FileSignature,
  ShieldCheck,
  Banknote,
  Rocket,
} from "lucide-react";

const STEPS = [
  {
    icon: Compass,
    title: "Discovery Call",
    copy: "A 30-minute strategy session with a senior advisor to understand your activity, ownership plan, and 5-year growth goals.",
    tag: "Day 1",
  },
  {
    icon: ClipboardList,
    title: "Structure Recommendation",
    copy: "We map Mainland vs Freezone, authority choice, licensing activities, office, and tax posture — all in a single transparent quote.",
    tag: "Day 2",
  },
  {
    icon: FileSignature,
    title: "Documentation & KYC",
    copy: "Shareholder KYC, UBO disclosures, MoA drafting, notarisation and power-of-attorney handled by our in-house team.",
    tag: "Week 1",
  },
  {
    icon: ShieldCheck,
    title: "License Issuance",
    copy: "Initial approvals, trade name reservation, establishment card and your official UAE trade license — ready to sign contracts.",
    tag: "Week 2–3",
  },
  {
    icon: Banknote,
    title: "Bank Account & Tax",
    copy: "Corporate bank introductions to tier-1 UAE banks, VAT & corporate tax registration, and ESR/UBO compliance setup.",
    tag: "Week 3–5",
  },
  {
    icon: Rocket,
    title: "Visa & Launch",
    copy: "Investor visa, Emirates ID, medical, and family sponsorship — your residency live so you can hire, lease and invoice.",
    tag: "Week 4–6",
  },
];

export default function ProcessTimeline() {
  return (
    <section
      data-testid="process-timeline"
      className="relative bg-white py-20 md:py-32"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <p className="overline text-[#3366FF]">How we work</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-[#0F172A] text-3xl sm:text-4xl lg:text-5xl leading-tight">
              From first call to{" "}
              <span className="font-black text-[#002366]">first invoice</span> — in as little
              as 6 weeks.
            </h2>
          </div>
          <p className="md:max-w-md text-slate-600 leading-relaxed">
            Every StartINC engagement follows the same disciplined 6-phase protocol. No
            guesswork, no shifting scope, no hidden fees — just a private-banking-grade
            execution of your UAE incorporation.
          </p>
        </div>

        <div className="relative">
          {/* vertical guide line */}
          <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-slate-200" />

          <ol className="space-y-6 md:space-y-0">
            {STEPS.map(({ icon: Icon, title, copy, tag }, i) => {
              const left = i % 2 === 0;
              return (
                <motion.li
                  key={title}
                  data-testid={`timeline-step-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="md:grid md:grid-cols-2 md:gap-12 md:py-6 relative"
                >
                  {/* dot */}
                  <span className="hidden md:block absolute left-1/2 -translate-x-1/2 top-10 w-3 h-3 rounded-full bg-[#3366FF] ring-4 ring-[#EEF2FF]" />

                  {/* content card */}
                  <div className={left ? "md:pr-10 md:text-right" : "md:col-start-2 md:pl-10"}>
                    <div
                      className={`inline-flex items-center gap-3 rounded-sm border border-slate-200 bg-white p-6 md:p-7 shadow-sm hover:shadow-xl hover:border-[#3366FF]/40 transition-all w-full ${
                        left ? "md:flex-row-reverse md:text-right" : ""
                      }`}
                    >
                      <div className="shrink-0 w-11 h-11 rounded-sm bg-[#EEF2FF] text-[#3366FF] inline-flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 justify-between">
                          <span className="overline text-[#002366]/70">
                            Step {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="chip">{tag}</span>
                        </div>
                        <h3 className="font-heading font-semibold text-lg md:text-xl tracking-tight text-[#0F172A]">
                          {title}
                        </h3>
                        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                          {copy}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
